import { Fragment } from "react";
import { Link, useLocation } from "react-router";
import { SidebarTrigger } from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { getPageMeta, NOT_FOUND_META, type Crumb } from "@/lib/pageMeta";

/** La raíz de toda miga. Va acá y no en cada entrada de `pageMeta`. */
const RAIZ: Crumb = { label: "Inicio", url: "/" };

/* La barra solo UBICA: migas y título de la pantalla, nada más. El selector de
   período, la campana de notificaciones y el ícono del Design System se
   retiraron de acá porque ninguno operaba sobre la pantalla que se veía.

   TODA pantalla pasa por acá con su título. El saludo ("Hola, <nombre> 👋") se
   fue con la portada: era la excepción de una sola pantalla, y esa pantalla ya
   no existe. */
export function TopBar() {
  const { pathname, search } = useLocation();
  /* Una ruta sin metadatos es un 404, y tiene su propio título: sin este
     respaldo el header no tendría qué mostrar arriba del cuerpo que dice que
     la página no existe. */
  // `search` incluido: hay pantallas a las que se llega desde más de un lugar
  // y cuyo camino de miga depende de cuál fue (ver `origenDeNavegacion.ts`).
  const meta = getPageMeta(pathname, search) ?? NOT_FOUND_META;

  /* `/` es la entrada real de la aplicación y redirige a `/informes`, así que
     "Inicio" lleva a alguna parte y no a una pantalla muerta.

     El respaldo por el título cubre a las pantallas de primer nivel, que son
     las que NO declaran ancestros: ahí la miga es "Inicio / <la pantalla>". */
  const camino: Crumb[] =
    meta.crumbs.length > 0
      ? [RAIZ, ...meta.crumbs]
      : [RAIZ, { label: meta.title }];

  return (
    /* SIN PADDING ABAJO, y esa es la corrección.
    
       El título y la bajada de cada pantalla estaban al doble de distancia de
       la que pide el diseño —45 px contra 6— y no porque nadie hubiera elegido
       45: eran DOS paddings sumados que nadie veía juntos, el `pb-4` de acá y
       el `pt` de la página. Cada uno se leía razonable en su archivo.
    
       Ahora el hueco lo pone UN SOLO lado: el `pt-2` de la página. Este header
       termina donde termina el título.
    
       `px-6 lg:px-8` se queda: es lo que alinea el título con el cuerpo en
       todos los anchos. */
    <header className="flex shrink-0 items-center gap-3 bg-background px-6 pt-7 lg:px-8">
      <SidebarTrigger className="md:hidden" />

      {/* Breadcrumbs + título (convención: el título va aquí).

          LA MIGA VA EN TODAS LAS PANTALLAS, y su raíz la pone ESTE archivo, no
          cada entrada de `pageMeta`: así una pantalla nueva la hereda sin que
          nadie se acuerde de escribirla.

          Cuando la pantalla no declara ancestros, su propia miga es su título.
          Antes en ese caso no se dibujaba ninguna —una miga sola repetía el
          título de abajo—, pero con "Inicio" delante ya no repite: ubica. */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Breadcrumb>
          <BreadcrumbList>
            {camino.map((crumb, i) => {
              const last = i === camino.length - 1;
              return (
                <Fragment key={`${crumb.label}-${i}`}>
                  <BreadcrumbItem>
                    {last ? (
                      <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                    ) : crumb.url ? (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.url}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                  </BreadcrumbItem>
                  {!last && <BreadcrumbSeparator />}
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
        {/* `text-h2` Y NO UN TAMAÑO A MANO, que es lo que había
            (`text-2xl … md:text-3xl` con su peso y su tracking sueltos).

            Dos cosas se arreglan de una: el nivel y la forma de pedirlo.

            EL NIVEL — el diseño titula sus pantallas con Heading 2 (22 px) y
            esto renderizaba 28, que es Heading 1. Un nivel de mas, en TODA
            pantalla, porque este `<h1>` es el titulo de todas.

            LA FORMA — la rampa del DS ya trae el peso (800) y el tracking
            (-0.01em) de cada nivel. Escribirlos al lado del tamaño es como se
            desincronizan: cambia la rampa y estos tres no se enteran. */}
        <h1 className="min-w-0 truncate text-h2">{meta.title}</h1>
      </div>
    </header>
  );
}
